import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient, getWalletClient, switchChain, signMessage } from "wagmi/actions";
import type { Address } from "viem";
import { keelReferidosAbi } from "../abi";
import type { RedKeel } from "../config/networks";
import { leeReferido, olvidaReferido } from "../lib/referido";

/**
 * Referidos: el alta, lo que ve quien invita y lo que ve quien fue invitado.
 *
 * DOS FUENTES, Y CADA UNA MANDA EN LO SUYO:
 *
 *   · LA CADENA manda en el dinero. `KeelReferidos` dice quien invito a quien y si ya cobra, y el
 *     reparto lo hace el vault al cobrar comisiones. Nada de lo que pase en el servidor mueve un
 *     centimo.
 *   · EL SERVICIO manda en la cuenta. Cuantos invitados, cuanto han generado, cuanto se ha pagado:
 *     todo eso sale de leer los eventos de las tres redes, y en cadena costaria un contador por
 *     wallet que no necesita nadie para que el dinero llegue.
 *
 * SI FALTA CUALQUIERA DE LAS DOS, LA PANTALLA SIGUE SIENDO HONESTA. Sin registro desplegado se dice
 * que el reparto todavia no esta activo en esa red; sin servicio, se pierden los numeros pero no el
 * alta. Lo que no se hace nunca es enseñar un cero como si fuera un dato.
 */

const BASE = import.meta.env.VITE_REFERIDOS_URL ?? "/api/referidos";

async function pedirJson(ruta: string, cuerpo?: unknown) {
  const r = await fetch(BASE + ruta, {
    method: cuerpo === undefined ? "GET" : "POST",
    headers: cuerpo === undefined ? undefined : { "Content-Type": "application/json" },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error ?? `el servidor de referidos respondio ${r.status}`);
  return d;
}

export type EstadoReferido = {
  /** quien me invito, segun la cadena; null si nadie o si esa red no tiene registro */
  referidorEnCadena: Address | null;
  /** quien me invito, segun el servicio (puede ir por delante: el alta se firma antes de subirla) */
  referidorEnServicio: Address | null;
  /** ya genere lo suficiente para que mi referidor cobre */
  activo: boolean;
  /** el registro de esta red esta desplegado */
  hayRegistro: boolean;
  /** un enlace pendiente de aceptar, guardado del `?ref=` */
  pendiente: Address | null;
};

export function useMiReferido(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();

  return useQuery({
    queryKey: ["referido", red?.clave, address],
    enabled: !!address,
    refetchInterval: 120_000,
    queryFn: async (): Promise<EstadoReferido> => {
      const registro = red?.referidos;
      let referidorEnCadena: Address | null = null;
      let activo = false;

      if (registro && red) {
        const cliente = getPublicClient(config, { chainId: red.idCadena })!;
        const c = { address: registro, abi: keelReferidosAbi } as const;
        const [ref, act] = await cliente.multicall({
          allowFailure: false,
          contracts: [
            { ...c, functionName: "referidorDe", args: [address!] },
            { ...c, functionName: "activo", args: [address!] },
          ],
        });
        const r = ref as Address;
        referidorEnCadena = r === "0x0000000000000000000000000000000000000000" ? null : r;
        activo = act as boolean;
      }

      // El servicio puede fallar (no desplegado, caido, sin nginx en `npm run dev`) y eso NO puede
      // vaciar lo que ya sabemos por la cadena.
      const delServicio = await pedirJson(`/estado?dueno=${address}`).catch(() => ({}));

      const pendiente = leeReferido();
      return {
        referidorEnCadena,
        referidorEnServicio: (delServicio.referidor as Address) ?? null,
        activo: activo || !!delServicio.activo,
        hayRegistro: !!registro,
        // Un enlace que apunta a uno mismo no es una invitacion: es alguien probando su propio
        // enlace. Se ignora aqui para no ofrecerle aceptar algo que el contrato va a rechazar.
        pendiente:
          pendiente && address && pendiente.toLowerCase() !== address.toLowerCase()
            ? pendiente
            : null,
      };
    },
  });
}

export type PanelReferidor = {
  invitados: number;
  activos: number;
  generado: Record<string, Record<string, string>>;
  pagado: Record<string, Record<string, string>>;
  /** Desglose por invitado CON su wallet: cuánto generó cada uno y si ya cuenta. */
  porInvitado?: { wallet: Address; activo: boolean; generado: Record<string, Record<string, string>> }[];
};

/** Lo que ve quien invita. Sale entero del servicio: son agregados, no dinero. */
export function usePanelReferidor() {
  const { address } = useAccount();
  return useQuery({
    queryKey: ["panel-referidor", address],
    enabled: !!address,
    retry: false,
    refetchInterval: 120_000,
    queryFn: async (): Promise<PanelReferidor> => pedirJson(`/panel?referidor=${address}`),
  });
}

/**
 * Aceptar una invitacion. Dos caminos, y el bueno depende de lo que haya desplegado:
 *
 *   · CON SERVICIO (lo normal): se firma un texto y ya esta. No cuesta gas, no hay que esperar a un
 *     bloque, y el alta la sube a la cadena el keeper. En HyperEVM, con un bloque por minuto, esto
 *     es la diferencia entre aceptar una invitacion y abandonar a medias.
 *   · SIN SERVICIO: se llama al registro desde la wallet. Cuesta gas y hay que esperar, pero
 *     funciona sin depender de nada nuestro — y eso importa: si un dia el servicio no esta, el
 *     referido no deja de existir.
 */
export function useAceptarInvitacion(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();
  const qc = useQueryClient();
  const [haciendo, setHaciendo] = useState<null | "firma" | "tx">(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState(false);

  async function refrescar() {
    await qc.invalidateQueries({ queryKey: ["referido"] });
  }

  async function conFirma(referidor: Address) {
    // El alta es una FIRMA sin gas (personal_sign), INDEPENDIENTE de la cadena: el backend recupera
    // al firmante esté en la red que esté. Se usa la acción `signMessage` de wagmi (no
    // `getWalletClient`) a propósito: pedir el wallet client obliga a una cadena concreta y revienta
    // —o ni abre el diálogo de firma— cuando la wallet está en una red que la config no conoce
    // (p. ej. Ethereum). `signMessage` firma con el conector tal cual, sin exigir cadena.
    if (!address) throw new Error("conecta la wallet");
    const { mensaje, caduca } = await pedirJson("/nonce", { dueno: address, referidor });
    const firma = await signMessage(config, { account: address, message: mensaje });
    await pedirJson("/alta", { dueno: address, referidor, caduca, firma });
  }

  async function conTransaccion(referidor: Address) {
    if (!red?.referidos) throw new Error("esta red todavia no tiene registro de referidos");
    // Esto SÍ es una transacción on-chain: hay que estar en la red del registro. Si la wallet está
    // en otra, se cambia antes (si no, wagmi da el mismo desajuste de cadena).
    await switchChain(config, { chainId: red.idCadena }).catch(() => {});
    const wallet = await getWalletClient(config, { chainId: red.idCadena });
    const cliente = getPublicClient(config, { chainId: red.idCadena })!;
    if (!wallet || !address) throw new Error("conecta la wallet");
    const hash = await wallet.writeContract({
      address: red.referidos,
      abi: keelReferidosAbi,
      functionName: "registrarPropio",
      args: [referidor],
      account: address,
      chain: null,
    });
    await cliente.waitForTransactionReceipt({ hash });
  }

  return {
    haciendo,
    error,
    hecho,
    limpiar: () => { setError(null); setHecho(false); },

    aceptar: async (referidor: Address) => {
      setError(null);
      try {
        setHaciendo("firma");
        await conFirma(referidor);
      } catch (e) {
        const m = e instanceof Error ? e.message : String(e);
        // Un rechazo en la wallet es alguien diciendo que no, no un fallo que haya que gritar — y no
        // tiene sentido reintentarlo por otro camino.
        if (/rejected|denied|User rejected/i.test(m)) {
          setHaciendo(null);
          return;
        }
        // El servicio no esta: se intenta por la cadena antes de darse por vencido.
        try {
          setHaciendo("tx");
          await conTransaccion(referidor);
        } catch (e2) {
          const m2 = e2 instanceof Error ? e2.message : String(e2);
          setError(/rejected|denied|User rejected/i.test(m2) ? null : m2);
          setHaciendo(null);
          return;
        }
      }
      // Guardado ya del lado bueno: el `?ref=` local ha cumplido y no tiene que sobrevivir a nada.
      olvidaReferido();
      setHecho(true);
      setHaciendo(null);
      await refrescar();
    },

    rechazar: async () => {
      olvidaReferido();
      await refrescar();
    },
  };
}
